/**
 * The record page's panel slots (13-25), in order. Each slot names the register verbs it runs and the registers it
 * applies to. The page shows a slot only when at least one of its verbs passes `canDoRecordVerb` for this record and
 * this person (13-22's gate: a panel a role cannot use is hidden, not disabled). A slot with no component yet renders
 * the disabled "arrives in the next update" card (the D-13-09-4 pattern) until its plan fills it:
 *
 *   triage (13-25) · handling (13-26: the handling or treat columns and the log composer), closure (13-26: the moves
 *   `closureVerbsFor` allows from the record's status) · reportability, notification (13-27) · whs, worksafe (13-28) ·
 *   audit (13-32: Internal Audits, not one of the five registers: `AUDIT_PANEL`, on the audit record view).
 *
 * The notification verb records one notice at a time (D-13-20-5, D-13-22-5), so its slots carry the notices they
 * record: NDIS and SIRS under notification, WorkSafe under worksafe (HR & WHS's, D-13-L-2).
 *
 * 13-27: a slot may also be **read** by someone its gate refuses (`readOnlyFor`): the reportability call and the
 * notices are shown read only to every other register role, with who decides, so a pending call is never hidden.
 * `panelViewsFor` gives the page both kinds, in slot order. The notification slot shows only once the decision has a
 * notice (`isNotifiable`).
 *
 * 13-28: the whs slot (WhsPanel: HR & WHS works it, every other register role reads it) and the worksafe slot
 * (WorkSafePanel: only once `WorkSafeNotifiable` = Yes or the decision is WorkSafe, `isWorkSafeNotice`).
 *
 * 13.2-07: the followUps slot (FollowUpsPanel) writes Action Register rows, not register verbs, so its gate is its own
 * `allowedFor` (`canAddFollowUp`) instead of the verb gate.
 *
 * 13.2-09: the complete slot (CompletePanel, D-13.2-P-3), first: a Care Manager or Oversight completes the report on
 * an open incident, intake stubs included. It is the person's next step only while the report lacks a field triage
 * can't add (`nextStepWhen`: `columnsToComplete`, the same test as Today's "Complete the report" and triage's gate);
 * on a complete report the next step stays triage. Today links it as `#panel-complete`.
 */

import type { ComponentType } from 'react';
import { verbsOf } from '../../../config/fieldset/registerFields';
import { RECORD_REGISTERS } from '../../../config/fieldset/live';
import { liveChoice } from '../../../config/fieldset/live';
import { canDoRecordVerb, holdsRegisterRole } from '../../../lib/recordAuthority';
import { closureVerbsFor } from '../../../lib/recordClosure';
import { isNotifiable } from '../../../lib/regulatoryPanels';
import { isWorkSafeNotice } from '../../../lib/whsPanels';
import type { NoticeKind, RecordAuthorityContext } from '../../../lib/recordAuthority';
import type { SiblingRow } from '../../../services/recordService';
import type { Actor } from '../../../types/actionRegister';
import type { RecordRegister, RecordRow, RecordVerbKey } from '../../../types/records';
import type { VerbSaved } from '../VerbForm';
import AuditPanel from './AuditPanel';
import type { AuditPanelProps } from './AuditPanel';
import { canAddFollowUp } from '../../../lib/followUps';
import { columnsToComplete } from '../../../lib/reportCompleteness';
import ClosurePanel from './ClosurePanel';
import CompletePanel from './CompletePanel';
import FollowUpsPanel from './FollowUpsPanel';
import HandlingPanel from './HandlingPanel';
import NotificationPanel from './NotificationPanel';
import ReportabilityPanel from './ReportabilityPanel';
import TriagePanel from './TriagePanel';
import WhsPanel from './WhsPanel';
import WorkSafePanel from './WorkSafePanel';

/** What every panel is given. */
export interface PanelProps {
  row: RecordRow;
  siblings: readonly SiblingRow[];
  actor: Actor;
  /** The record's open Closure Links (refs), for a closing step's confirm (D-13-L-14). */
  openLinks?: readonly string[];
  /** 13-27: the slot is shown to read only (its gate refuses this person; `readOnlyFor` allows the view). */
  readOnly?: boolean;
  onSaved: (saved: VerbSaved) => void;
}

export type PanelKey = 'complete' | 'triage' | 'handling' | 'followUps' | 'closure' | 'reportability' | 'notification' | 'whs' | 'worksafe' | 'audit';

export interface PanelSlot {
  key: PanelKey;
  title: string;
  verbs: readonly RecordVerbKey[];
  registers: readonly RecordRegister[];
  /** The notification verb's notices this slot records (its gate is asked once per notice). */
  notices?: readonly NoticeKind[];
  /**
   * The verbs the record's state allows now (13-26: the closure slot follows `closureVerbsFor`, so a Completed record
   * offers only Reopen). Default: every verb in `verbs`; the state questions stay with the builders.
   */
  verbsFor?: (row: RecordRow) => readonly RecordVerbKey[];
  /**
   * 13-27: who may read the slot when its gate refuses them (the panel renders read only). Default: nobody.
   */
  readOnlyFor?: (row: RecordRow, actor: Actor) => boolean;
  /**
   * 13.2-07: who may act in a slot whose writes are not register verbs (`verbs: []`; the Follow-ups slot writes Action
   * Register rows). Used instead of the verb gate when set. Default: the verb gate.
   */
  allowedFor?: (row: RecordRow, actor: Actor) => boolean;
  /**
   * 13.2-09: whether the slot holds work waiting on this record, for the record page's next step (`openByDefault`).
   * Default: always (the stage rule decides). The complete slot is waiting only while the report lacks a field.
   */
  nextStepWhen?: (row: RecordRow) => boolean;
  /** `null` = built by a later plan: shown disabled, "arrives in the next update". */
  component: ComponentType<PanelProps> | null;
}

const [COMPLETED, CANCELLED] = liveChoice('incidents', 'Status', 'Completed', 'Cancelled');
for (const register of RECORD_REGISTERS) liveChoice(register, 'Status', COMPLETED, CANCELLED);

/** The record is Completed or Cancelled (the same two values on every register, checked at load). */
export function isClosedRecord(row: RecordRow): boolean {
  const status = row.fields.Status;
  return status === COMPLETED || status === CANCELLED;
}

/**
 * D-13-25-7 / D-13-26-7, ruled 13 Oct 2026 (D-13-33): triage and handling are hidden on a closed record. A closed
 * record changes only through Reopen (Oversight, under Closure); a cancelled one is never reopened (§1.8).
 */
const openOnly = (verb: RecordVerbKey) => (row: RecordRow): readonly RecordVerbKey[] => (isClosedRecord(row) ? [] : [verb]);

export const PANEL_SLOTS: readonly PanelSlot[] = [
  {
    // 13.2-09 (D-13.2-A-1, D-13.2-P-3): the CM (or Oversight) completes the report after lodge, an intake stub included.
    // Hidden on a closed record (a completed report changes only after Oversight reopens it).
    key: 'complete',
    title: 'Complete the report',
    verbs: ['complete'],
    registers: ['incidents'],
    verbsFor: openOnly('complete'),
    nextStepWhen: (row) => columnsToComplete(row).length > 0,
    component: CompletePanel,
  },
  { key: 'triage', title: 'Triage', verbs: ['triage'], registers: RECORD_REGISTERS, verbsFor: openOnly('triage'), component: TriagePanel },
  {
    key: 'reportability',
    title: 'Reportability',
    verbs: ['reportability'],
    registers: ['incidents'],
    readOnlyFor: (_row, actor) => holdsRegisterRole(actor),
    component: ReportabilityPanel,
  },
  {
    key: 'notification',
    title: 'Regulator notices',
    verbs: ['notification'],
    registers: ['incidents'],
    notices: ['ndis', 'sirs'],
    // Shown only once the decision has a notice (13-27); read only to every other register role.
    verbsFor: (row) => (isNotifiable(row) ? ['notification'] : []),
    readOnlyFor: (row, actor) => isNotifiable(row) && holdsRegisterRole(actor),
    component: NotificationPanel,
  },
  {
    key: 'whs',
    title: 'WHS',
    verbs: ['whs'],
    registers: ['incidents', 'risk'],
    // 13-28: HR & WHS works it; every other register role reads it ("HR & WHS handles the WHS branch").
    readOnlyFor: (_row, actor) => holdsRegisterRole(actor),
    component: WhsPanel,
  },
  {
    key: 'worksafe',
    title: 'WorkSafe notice',
    verbs: ['notification'],
    registers: ['incidents'],
    notices: ['worksafe'],
    // 13-28: only once the WHS answer is Yes or the decision is WorkSafe; read only to every other register role.
    verbsFor: (row) => (isWorkSafeNotice(row) ? ['notification'] : []),
    readOnlyFor: (row, actor) => isWorkSafeNotice(row) && holdsRegisterRole(actor),
    component: WorkSafePanel,
  },
  {
    key: 'handling',
    title: 'Handling and actions',
    verbs: ['handling'],
    registers: RECORD_REGISTERS,
    verbsFor: openOnly('handling'),
    component: HandlingPanel,
  },
  {
    // 13.2-07 (D-13.2-A-4, D-13.2-P-7): dated follow-ups, written as Action Register rows. CareManager, HRWHS or
    // Oversight add them on an open incident (`canAddFollowUp`); every register role reads them, and a closed incident
    // shows them read only. No register verb, so it is never the default next step (D-13.2-11-2).
    key: 'followUps',
    title: 'Follow-ups',
    verbs: [],
    registers: ['incidents'],
    allowedFor: canAddFollowUp,
    readOnlyFor: (_row, actor) => holdsRegisterRole(actor),
    component: FollowUpsPanel,
  },
  {
    key: 'closure',
    title: 'Closure',
    // 13.2-10: closeAsDuplicate (Oversight, Incidents) is a closure step with its own form in ClosurePanel.
    verbs: ['readyForApproval', 'approveAndClose', 'returnForRework', 'close', 'ciApprove', 'riskReview', 'riskRetire', 'cancel', 'closeAsDuplicate', 'reopen'],
    registers: RECORD_REGISTERS,
    verbsFor: closureVerbsFor,
    component: ClosurePanel,
  },
  // 13-32: Internal Audits is Oversight's list, not one of the five registers, so this slot never shows on a register
  // record; the audit record view (`/record/AUD-<id>`) renders `AUDIT_PANEL` in its place.
  { key: 'audit', title: 'Internal audit', verbs: [], registers: [], component: null },
];

/**
 * 13-32: the `audit` slot's panel, for the Internal Audits record view. Its verbs are `lib/auditVerbs.ts`'s, gated by
 * `canDoAuditVerb` (Oversight only), so it takes its own props rather than a register's `PanelProps`.
 */
export const AUDIT_PANEL: { key: 'audit'; title: string; component: ComponentType<AuditPanelProps> } = {
  key: 'audit',
  title: 'Internal audit',
  component: AuditPanel,
};

/** Every gate context a slot's verbs are asked under (one per notice for the notification slots). */
function contextsOf(slot: PanelSlot, base: RecordAuthorityContext): RecordAuthorityContext[] {
  return slot.notices && slot.notices.length > 0 ? slot.notices.map((notice) => ({ ...base, notice })) : [base];
}

/** May this person use the slot on this record? (One of its verbs passes the gate.) */
export function slotAllowed(slot: PanelSlot, row: RecordRow, actor: Actor, base: RecordAuthorityContext = {}): boolean {
  if (!slot.registers.includes(row.register)) return false;
  if (slot.allowedFor) return slot.allowedFor(row, actor);
  const own = verbsOf(row.register);
  const now = slot.verbsFor ? slot.verbsFor(row) : slot.verbs;
  return slot.verbs.some(
    (verb) => own.includes(verb) && now.includes(verb) && contextsOf(slot, base).some((ctx) => canDoRecordVerb(verb, row, actor, ctx).ok),
  );
}

/**
 * On a closed record, the line that says why triage and handling are not shown (D-13-33), for someone whose gate
 * would allow either if the record were open; otherwise `null`.
 */
export function closedRecordLine(row: RecordRow, actor: Actor, base: RecordAuthorityContext = {}): string | null {
  if (!isClosedRecord(row)) return null;
  const own = verbsOf(row.register);
  const would = (['triage', 'handling'] as const).some((verb) => own.includes(verb) && canDoRecordVerb(verb, row, actor, base).ok);
  if (!would) return null;
  return row.fields.Status === COMPLETED
    ? 'This record is Completed, so its triage and handling are closed. To change them, Oversight reopens it first (under Closure).'
    : 'This record was cancelled, so its triage and handling stay as they are. A cancelled record is not reopened.';
}

/** The slots this person sees on this record, in order. */
export function panelsFor(row: RecordRow, actor: Actor, base: RecordAuthorityContext = {}): PanelSlot[] {
  return PANEL_SLOTS.filter((slot) => slotAllowed(slot, row, actor, base));
}

/** One slot as the page shows it: to act on, or to read only (13-27). */
export interface PanelView {
  slot: PanelSlot;
  readOnly: boolean;
}

/** Every slot this person sees on this record, in order: the ones they act on, and the ones they may only read. */
export function panelViewsFor(row: RecordRow, actor: Actor, base: RecordAuthorityContext = {}): PanelView[] {
  const out: PanelView[] = [];
  for (const slot of PANEL_SLOTS) {
    if (slotAllowed(slot, row, actor, base)) out.push({ slot, readOnly: false });
    else if (slot.component && slot.readOnlyFor && slot.registers.includes(row.register) && slot.readOnlyFor(row, actor)) {
      out.push({ slot, readOnly: true });
    }
  }
  return out;
}
